import TimeSeriesChart from './components/TimeSeriesChart';
import MetricExplorerChart from './components/MetricExplorerChart';
import TopSkusTable from './components/TopSkusTable';
import DailyBreakdownTable from './components/DailyBreakdownTable';
import { formatMetric, isBlockHidden } from './panelConfig';
import './EcomPanel.css';

const SPEND_SERIES = [
  { dataKey: 'spend_google', label: 'Google', color: '#f59e0b', yAxisId: 'left', format: 'currency' },
  { dataKey: 'spend_meta', label: 'Meta', color: '#3b82f6', yAxisId: 'left', format: 'currency' },
];

// Bloques de eficiencia unificados (antes dos grupos sueltos CR/Ticket/Ses + CPC/CTR/CPM).
const EFFICIENCY = [
  { key: 'conversion_rate', label: 'Conversión', format: 'percent', better: 'up' },
  { key: 'avg_ticket', label: 'Ticket', format: 'currency', better: 'up' },
  { key: 'sessions', label: 'Sesiones', format: 'number', better: 'up' },
  { key: 'cpc', label: 'CPC', format: 'currency2', better: 'down' },
  { key: 'ctr', label: 'CTR', format: 'percent', better: 'up' },
  { key: 'cpm', label: 'CPM', format: 'currency2', better: 'down' },
];

function Delta({ value, previousValue, better = 'up' }) {
  if (value == null || previousValue == null || !previousValue) return null;
  const delta = (value - previousValue) / previousValue;
  const up = delta >= 0;
  const favorable = better === 'neutral' ? null : (better === 'up') === up;
  const cls = favorable === null ? 'flat' : favorable ? 'up' : 'down';
  const pct = Math.abs(delta * 100).toLocaleString('es-UY', { maximumFractionDigits: 1 });
  return <span className={`ecp-delta ${cls}`}>{up ? '▲ +' : '▼ −'}{pct}%</span>;
}

/**
 * Panel de ecommerce (Resumen) con la estética del reporte (Sora/Manrope, stat
 * strip, tablas finas). Franja de resultado, gráfico explorador, eficiencia
 * unificada, inversión Google vs Meta, Top 10 productos y detalle diario.
 * Cada bloque es apagable por marca vía BRAND_HIDDEN_BLOCKS (isBlockHidden).
 * `previousSummary` alimenta los deltas.
 */
export default function EcomPanel({ data, previousSummary }) {
  const prev = previousSummary ?? {};
  const { summary = {}, daily = [], top_skus: topSkus = [] } = data;
  const brandId = data?.brand?.id;
  const show = (key) => !isBlockHidden(brandId, key);

  return (
    <div className="ecp">
      {/* Franja de resultado */}
      {show('revenue_strip') && (
        <div className="ecp-strip">
          <div className="ecp-stat">
            <div className="k">Revenue</div>
            <div className="v">{formatMetric(summary.revenue, 'currency')}</div>
            <Delta value={summary.revenue} previousValue={prev.revenue} better="up" />
          </div>
          <div className="ecp-stat">
            <div className="k">Órdenes</div>
            <div className="v">{formatMetric(summary.orders, 'number')}</div>
            <Delta value={summary.orders} previousValue={prev.orders} better="up" />
          </div>
          <div className="ecp-stat">
            <div className="k">ROAS</div>
            <div className="v">{formatMetric(summary.roas, 'ratio')}</div>
            <Delta value={summary.roas} previousValue={prev.roas} better="up" />
          </div>
          <div className="ecp-stat">
            <div className="k">Inversión</div>
            <div className="v">{formatMetric(summary.spend, 'currency')}</div>
            <Delta value={summary.spend} previousValue={prev.spend} better="neutral" />
          </div>
        </div>
      )}

      {/* Gráfico principal */}
      {show('metric_explorer') && (
        <div className="ecp-explorer"><MetricExplorerChart daily={daily} /></div>
      )}

      {/* Eficiencia (unificada) */}
      {show('efficiency') && (
        <div className="ecp-panel">
          <div className="ecp-ph">Eficiencia</div>
          <div className="ecp-mini">
            {EFFICIENCY.map((m) => (
              <div className="m" key={m.key}>
                <div className="k">{m.label}</div>
                <div className="v">{formatMetric(summary[m.key], m.format)}</div>
                <Delta value={summary[m.key]} previousValue={prev[m.key]} better={m.better} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inversión Google vs Meta */}
      {show('spend_chart') && (
        <div className="ecp-panel">
          <div className="ecp-ph">Inversión Google vs Meta</div>
          <TimeSeriesChart data={daily} series={SPEND_SERIES} card={false} />
        </div>
      )}

      {/* Top 10 productos */}
      {show('top_skus') && topSkus.length > 0 && (
        <div className="ecp-panel">
          <div className="ecp-ph">Top 10 productos</div>
          <div className="ecp-tablewrap"><TopSkusTable skus={topSkus} /></div>
        </div>
      )}

      {/* Detalle diario */}
      {show('daily_breakdown') && (
        <div className="ecp-panel">
          <div className="ecp-ph">Detalle diario</div>
          {daily.length > 0 ? (
            <div className="ecp-tablewrap"><DailyBreakdownTable daily={daily} /></div>
          ) : (
            <p className="ecp-empty">Sin datos para el rango seleccionado.</p>
          )}
        </div>
      )}
    </div>
  );
}
